import { useEffect } from 'preact/hooks';
const COUNTER_ATTR = 'data-scroll-lock-count';
const OVERLAY_ATTR = 'data-asset-overlay';
/**
 * Locks `body` scroll while the calling component is mounted. Multiple
 * concurrent locks are reference-counted via a `data-scroll-lock-count`
 * attribute, so:
 *
 *  - Strict-mode double-mount won't leak state.
 *  - Hot module reloads can't strand `overflow: hidden` after cleanup.
 *  - Two overlays open at once (e.g. review opens builder) cleanly compose:
 *    the body unlocks only when the last lock releases.
 *
 * Replaces the older "capture prev → restore on cleanup" pattern, which was
 * fragile under React strict mode / Vite HMR — a second mount would capture
 * `prev='hidden'` from the first mount's effect and "restore" the body to a
 * locked state on cleanup, leaving the entire app un-scrollable.
 */
export function useBodyScrollLock(enabled = true) {
    useEffect(() => {
        if (!enabled)
            return;
        const body = document.body;
        const current = parseInt(body.getAttribute(COUNTER_ATTR) ?? '0', 10) || 0;
        const next = current + 1;
        body.setAttribute(COUNTER_ATTR, String(next));
        body.style.overflow = 'hidden';
        body.setAttribute(OVERLAY_ATTR, '');
        return () => {
            const after = (parseInt(body.getAttribute(COUNTER_ATTR) ?? '0', 10) || 0) - 1;
            if (after <= 0) {
                body.removeAttribute(COUNTER_ATTR);
                body.style.overflow = '';
                body.removeAttribute(OVERLAY_ATTR);
            }
            else {
                body.setAttribute(COUNTER_ATTR, String(after));
            }
        };
    }, [enabled]);
}
